import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import Button from '../../../../components/Button/Button';
import {
  usePostSectionMutation,
  useUpdateSectionMutation,
} from '../../../../services/plaintiffSheets/plaintiffSheetsEndpoint';
import { ISectionItem } from '../../../../services/plaintiffSheets/plaintiffSheetsTypes';
import { useParams } from 'react-router-dom';
import { useToast } from '../../../../components/Toast/ToastContext';

interface IAddSectionProps {
  closePopUp: () => void;
  data?: ISectionItem;
  factSheetId?: string;
  onSectionAdded?: (newSectionId: string) => void;
}

interface FormValues {
  sectionName: string;
  description: string;
}

const AddSection: React.FC<IAddSectionProps> = ({
  closePopUp,
  data,
  factSheetId,
  onSectionAdded,
}) => {
  const { addToast } = useToast();
  const { id } = useParams();
  const [addSection, { isLoading: isAdding }] = usePostSectionMutation();
  const [updateSection, { isLoading: isUpdating }] = useUpdateSectionMutation();

  // Store the original data for when we need to reset
  const [originalValues] = useState<FormValues>({
    sectionName: data?.title || '',
    description: data?.description || '',
  });

  const isEditMode = Boolean(data?.section_id); // or use another unique key
  const isLoading = isAdding || isUpdating;

  // Submit handler
  const handleSubmitSection = async (sectionName: string, description: string) => {
    try {
      const sectionData = {
        name: sectionName,
        description,
      };

      if (isEditMode && factSheetId) {
        const response = await updateSection({
          factSheetId: factSheetId,
          sectionId: data?.section_id,
          data: sectionData,
        }).unwrap();
        if (response.status === 'success') {
          addToast('Section Updated', 'Section is updated successfully.', 'success');
        } else {
          addToast('Section Update Failed', 'Failed to update section. Please try again.', 'error');
        }
      } else {
        const response = await addSection({
          factSheetId: id ?? '',
          data: sectionData,
        }).unwrap();

        if (response.status === 'success') {
          addToast('Section Added', 'Section is added successfully.', 'success');
          if (onSectionAdded && response.data?.id) {
            onSectionAdded(response.data.id);
          }
        } else {
          addToast('Section Addition Failed', 'Failed to add section. Please try again. ', 'error');
        }
      }

      closePopUp();
    } catch (error) {
      addToast('Section Added', 'Section is added successfully.', 'success');
    }
  };

  // Custom reset handler to clear fields regardless of edit mode
  const handleClearForm = (
    resetForm: (nextState?: Partial<FormikHelpers<FormValues>['resetForm']>) => void
  ) => {
    resetForm({
      values: {
        sectionName: '',
        description: '',
      },
    });
  };

  return (
    <div className="h-full flex flex-col">
      <Formik
        initialValues={originalValues}
        validationSchema={Yup.object().shape({
          sectionName: Yup.string().required('Please provide a relevant section name.'),
        })}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values, { setSubmitting }) => {
          handleSubmitSection(values.sectionName, values.description);
          setSubmitting(false);
        }}
      >
        {({ resetForm, errors }) => (
          <Form className="flex flex-col flex-grow">
            <div className="flex-grow">
              <div className="mb-4">
                <label htmlFor="sectionName" className="text-sm font-medium text-ds-gray-500">
                  Section Name <span className="text-red-500">*</span>
                </label>
                <Field
                  id="sectionName"
                  type="text"
                  name="sectionName"
                  placeholder="Please provide a relevant section name"
                  className={`w-full border border-2 rounded-md p-2 mt-2 outline-none placeholder:text-ds-gray-300 text-sm ${
                    errors.sectionName ? 'border-ds-red-400' : 'border-ds-gray-100'
                  }`}
                />
                <ErrorMessage
                  name="sectionName"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="description" className="text-sm font-medium text-ds-gray-500">
                  Description
                </label>
                <Field
                  as="textarea"
                  id="description"
                  name="description"
                  placeholder="Enter the description. i.e - This section provides key points of contact for communication and support."
                  className="w-full border border-2 border-ds-gray-100 rounded-md p-2 mt-2 outline-none placeholder:text-ds-gray-300 text-sm h-48 resize-none"
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-between mt-6 pt-4">
              <Button
                color="outlined"
                size="sm"
                onClick={() => handleClearForm(resetForm)}
                type="button"
              >
                Clear All
              </Button>
              <div className="flex gap-2">
                <Button color="outlined" size="sm" onClick={closePopUp} type="button">
                  Cancel
                </Button>
                <Button size="sm" color="primary" type="submit" loading={isLoading}>
                  {isEditMode ? 'Update' : 'Add'}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddSection;
